/**
 * Asset Service
 * API client methods for marketing asset management
 */

import axios, { AxiosInstance } from 'axios';
import { MarketingAsset, AssetMetadata, AssetType, AssetStatus } from '../types/asset-metadata';

class AssetService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api'}/assets-management`,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Create a new marketing asset
   */
  async createAsset(file: File, metadata: AssetMetadata): Promise<MarketingAsset> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('metadata', JSON.stringify(metadata));

    const response = await this.api.post<{
      success: boolean;
      asset_id: string;
      metadata: AssetMetadata;
      file_path: string;
      file_size: number;
    }>('/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Fetch the created asset
    return this.getAsset(response.data.asset_id);
  }

  /**
   * Get a single asset by ID
   */
  async getAsset(assetId: string): Promise<MarketingAsset> {
    const response = await this.api.get<{
      asset: MarketingAsset;
    }>(`/${assetId}`);

    return response.data.asset;
  }

  /**
   * List assets with optional filtering
   */
  async listAssets(params?: {
    type?: AssetType;
    status?: AssetStatus;
    campaign_id?: string;
    owner_id?: string;
    tags?: string[];
    limit?: number;
    offset?: number;
  }): Promise<{
    assets: MarketingAsset[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const queryParams = new URLSearchParams();

    if (params?.type) queryParams.append('type', params.type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.campaign_id) queryParams.append('campaign_id', params.campaign_id);
    if (params?.owner_id) queryParams.append('owner_id', params.owner_id);
    if (params?.tags) queryParams.append('tags', params.tags.join(','));
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const response = await this.api.get<{
      assets: MarketingAsset[];
      total: number;
      limit: number;
      offset: number;
    }>(`?${queryParams.toString()}`);

    return response.data;
  }

  /**
   * Update an existing asset
   */
  async updateAsset(
    assetId: string,
    updates: {
      file?: File;
      metadata?: Partial<AssetMetadata>;
    }
  ): Promise<MarketingAsset> {
    const formData = new FormData();

    if (updates.file) {
      formData.append('file', updates.file);
    }

    if (updates.metadata) {
      formData.append('metadata', JSON.stringify(updates.metadata));
    }

    const response = await this.api.put<{
      success: boolean;
      asset_id: string;
      version: number;
      metadata: AssetMetadata;
    }>(`/${assetId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Fetch the updated asset
    return this.getAsset(assetId);
  }

  /**
   * Delete (archive) an asset
   */
  async deleteAsset(assetId: string): Promise<void> {
    await this.api.delete(`/${assetId}`);
  }

  /**
   * Get asset version history
   */
  async getAssetVersions(assetId: string): Promise<{
    asset_id: string;
    versions: Array<{
      id: number;
      asset_id: string;
      version_number: number;
      file_path: string;
      metadata: string;
      created_at: string;
      created_by: string;
      change_description: string;
    }>;
  }> {
    const response = await this.api.get(`/${assetId}/versions`);
    return response.data;
  }

  /**
   * Duplicate an asset
   */
  async duplicateAsset(
    assetId: string,
    options?: {
      title?: string;
      campaign_id?: string;
    }
  ): Promise<MarketingAsset> {
    const response = await this.api.post<{
      success: boolean;
      asset_id: string;
      metadata: AssetMetadata;
    }>(`/${assetId}/duplicate`, options);

    return this.getAsset(response.data.asset_id);
  }

  /**
   * Download asset file
   */
  async downloadAsset(assetId: string): Promise<Blob> {
    const asset = await this.getAsset(assetId);

    if (!asset.presigned_url) {
      throw new Error('No download URL available');
    }

    const response = await axios.get(asset.presigned_url, {
      responseType: 'blob',
    });

    return response.data;
  }

  /**
   * Search assets by query
   */
  async searchAssets(query: string): Promise<MarketingAsset[]> {
    const response = await this.api.get<{
      assets: MarketingAsset[];
    }>(`/search?q=${encodeURIComponent(query)}`);

    return response.data.assets;
  }

  /**
   * Get assets by campaign
   */
  async getAssetsByCampaign(campaignId: string): Promise<MarketingAsset[]> {
    const result = await this.listAssets({ campaign_id: campaignId });
    return result.assets;
  }

  /**
   * Get assets by type
   */
  async getAssetsByType(type: AssetType): Promise<MarketingAsset[]> {
    const result = await this.listAssets({ type });
    return result.assets;
  }

  /**
   * Batch update asset status
   */
  async batchUpdateStatus(
    assetIds: string[],
    status: AssetStatus
  ): Promise<{
    success: boolean;
    updated: string[];
    failed: string[];
  }> {
    const response = await this.api.post('/batch/status', {
      asset_ids: assetIds,
      status,
    });

    return response.data;
  }

  /**
   * Get asset statistics
   */
  async getAssetStats(): Promise<{
    total: number;
    by_type: Record<AssetType, number>;
    by_status: Record<AssetStatus, number>;
    recent: MarketingAsset[];
  }> {
    const response = await this.api.get('/stats');
    return response.data;
  }

  /**
   * Create asset from HTML content (for text-based assets)
   */
  async createHTMLAsset(
    type: 'email' | 'seo_content' | 'faq' | 'site_content',
    htmlContent: string,
    metadata: AssetMetadata
  ): Promise<MarketingAsset> {
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
    const file = new File([htmlBlob], `${type}_${Date.now()}.html`, { type: 'text/html' });

    return this.createAsset(file, metadata);
  }

  /**
   * Upload multiple assets
   */
  async uploadBatch(
    assets: Array<{
      file: File;
      metadata: AssetMetadata;
    }>
  ): Promise<{
    success: MarketingAsset[];
    failed: Array<{ file: string; error: string }>;
  }> {
    const results = await Promise.allSettled(
      assets.map(({ file, metadata }) => this.createAsset(file, metadata))
    );

    const success: MarketingAsset[] = [];
    const failed: Array<{ file: string; error: string }> = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        success.push(result.value);
      } else {
        failed.push({
          file: assets[index].file.name,
          error: result.reason.message,
        });
      }
    });

    return { success, failed };
  }
}

// Export singleton instance
export const assetService = new AssetService();

// Export class for testing
export default AssetService;